import React, { useState } from 'react';
import FormGroup from '../common/FormGroup';
import { validators,validateForm } from '../../utils/Validation';

const AddressForm = ({ formData, setFormData, errors, setErrors, touched, setTouched }) => {
  const [sameAsPermanent, setSameAsPermanent] = useState(false);

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleBlur = (field, validator) => {
    setTouched({ ...touched, [field]: true });
    if (validator) {
      const error = validator(formData[field]);
      setErrors({ ...errors, [field]: error });
    }
  };

  const handleSameAsPermanent = (e) => {
    const checked = e.target.checked;
    setSameAsPermanent(checked);
    
    if (checked) {
      setFormData({
        ...formData,
        corrAddress1: formData.permAddress1,
        corrAddress2: formData.permAddress2,
        corrCity: formData.permCity,
        corrZipcode: formData.permZipcode,
        corrState: formData.permState,
        corrCountry: formData.permCountry,
      });
    }
  };

  const handleNumberInput = (value) => {
    return value.replace(/\D/g, '');
  };

  const handleCityInput = (value) => {
    return value.replace(/[^a-zA-Z\s]/g, '');
  };

  // Capitalise first letter of each word (city / state / country)
  const capitaliseWords = (value) => {
    const cleaned = value.replace(/[^a-zA-Z\s]/g, '');
    return cleaned.replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  // Capitalise only the very first letter (address lines)
  const capitaliseFirst = (value) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '50px' }}>
      <div>
        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '25px', color: 'var(--primary-navy)' }}>
          Permanent Address
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          <FormGroup label="Address Line 1 *" error={touched.permAddress1 && errors.permAddress1}>
            <input 
              type="text" 
              className={`form-input ${errors.permAddress1 ? 'form-input-error' : ''}`}
              placeholder="Street address" 
              value={formData.permAddress1 || ''}
              onChange={(e) => handleInputChange('permAddress1', capitaliseFirst(e.target.value))}
              onBlur={() => handleBlur('permAddress1', validators.capitalFirstAddress)}
            />
          </FormGroup>
          
          <FormGroup label="Address Line 2">
            <input 
              type="text" 
              className="form-input" 
              placeholder="Apartment, suite, etc." 
              value={formData.permAddress2 || ''}
              onChange={(e) => handleInputChange('permAddress2', capitaliseFirst(e.target.value))}
            />
          </FormGroup>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormGroup label="City *" error={touched.permCity && errors.permCity}>
              <input 
                type="text" 
                className={`form-input ${errors.permCity ? 'form-input-error' : ''}`}
                placeholder="City" 
                value={formData.permCity || ''}
                onChange={(e) => handleInputChange('permCity', capitaliseWords(e.target.value))}
                onBlur={() => handleBlur('permCity', validators.capitalFirst)}
              />
            </FormGroup>
            
            <FormGroup label="Pincode *" error={touched.permZipcode && errors.permZipcode}>
              <input 
                type="text" 
                className={`form-input ${errors.permZipcode ? 'form-input-error' : ''}`}
                placeholder="6-digit pincode" 
                value={formData.permZipcode || ''}
                onChange={(e) => handleInputChange('permZipcode', handleNumberInput(e.target.value))}
                onBlur={() => handleBlur('permZipcode', validators.pincode)}
                maxLength="6"
              />
            </FormGroup>
          </div>
          
          <FormGroup label="State *" error={touched.permState && errors.permState}>
            <input 
              type="text" 
              className={`form-input ${errors.permState ? 'form-input-error' : ''}`}
              placeholder="State" 
              value={formData.permState || ''}
              onChange={(e) => handleInputChange('permState', capitaliseWords(e.target.value))}
              onBlur={() => handleBlur('permState', validators.capitalFirst)}
            />
          </FormGroup>
          
          <FormGroup label="Country *" error={touched.permCountry && errors.permCountry}>
            <input 
              type="text" 
              className={`form-input ${errors.permCountry ? 'form-input-error' : ''}`}
              placeholder="Country" 
              value={formData.permCountry || ''}
              onChange={(e) => handleInputChange('permCountry', capitaliseWords(e.target.value))}
              onBlur={() => handleBlur('permCountry', validators.capitalFirst)}
            />
          </FormGroup>
        </div>
      </div>
      
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--primary-navy)' }}>
            Correspondence Address
          </h4>
          <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={sameAsPermanent}
              onChange={handleSameAsPermanent}
              style={{ cursor: 'pointer' }}
            />
            Same as Permanent
          </label>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          <FormGroup label="Address Line 1 *" error={touched.corrAddress1 && errors.corrAddress1}>
            <input 
              type="text" 
              className={`form-input ${errors.corrAddress1 ? 'form-input-error' : ''}`}
              placeholder="Street address" 
              value={formData.corrAddress1 || ''}
              onChange={(e) => handleInputChange('corrAddress1', capitaliseFirst(e.target.value))}
              onBlur={() => handleBlur('corrAddress1', validators.capitalFirstAddress)}
              disabled={sameAsPermanent}
            />
          </FormGroup>
          
          <FormGroup label="Address Line 2">
            <input 
              type="text" 
              className="form-input" 
              placeholder="Apartment, suite, etc." 
              value={formData.corrAddress2 || ''}
              onChange={(e) => handleInputChange('corrAddress2', capitaliseFirst(e.target.value))}
              disabled={sameAsPermanent}
            />
          </FormGroup>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormGroup label="City *" error={touched.corrCity && errors.corrCity}>
              <input 
                type="text" 
                className={`form-input ${errors.corrCity ? 'form-input-error' : ''}`}
                placeholder="City" 
                value={formData.corrCity || ''}
                onChange={(e) => handleInputChange('corrCity', capitaliseWords(e.target.value))}
                onBlur={() => handleBlur('corrCity', validators.capitalFirst)}
                disabled={sameAsPermanent}
              />
            </FormGroup>
            
            <FormGroup label="Pincode *" error={touched.corrZipcode && errors.corrZipcode}>
              <input 
                type="text" 
                className={`form-input ${errors.corrZipcode ? 'form-input-error' : ''}`}
                placeholder="6-digit pincode" 
                value={formData.corrZipcode || ''}
                onChange={(e) => handleInputChange('corrZipcode', handleNumberInput(e.target.value))}
                onBlur={() => handleBlur('corrZipcode', validators.pincode)}
                maxLength="6"
                disabled={sameAsPermanent}
              />
            </FormGroup>
          </div>
          
          <FormGroup label="State *" error={touched.corrState && errors.corrState}>
            <input 
              type="text" 
              className={`form-input ${errors.corrState ? 'form-input-error' : ''}`}
              placeholder="State" 
              value={formData.corrState || ''}
              onChange={(e) => handleInputChange('corrState', capitaliseWords(e.target.value))}
              onBlur={() => handleBlur('corrState', validators.capitalFirst)}
              disabled={sameAsPermanent}
            />
          </FormGroup>
          
          <FormGroup label="Country *" error={touched.corrCountry && errors.corrCountry}>
            <input 
              type="text" 
              className={`form-input ${errors.corrCountry ? 'form-input-error' : ''}`}
              placeholder="Country" 
              value={formData.corrCountry || ''}
              onChange={(e) => handleInputChange('corrCountry', capitaliseWords(e.target.value))}
              onBlur={() => handleBlur('corrCountry', validators.capitalFirst)}
              disabled={sameAsPermanent}
            />
          </FormGroup>
        </div>
      </div>
    </div>
  );
};

export default AddressForm;